import { useState, useEffect } from 'react';
import { UserCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../supabaseClient';
import toast from 'react-hot-toast';

interface User {
  id: string;
  email: string;
  name: string;
  role?: string;
  canApprove?: boolean;
  canEditTemplates?: boolean;
  department?: string;
  title?: string;
  externalId?: string;
  lastSignIn?: string;
  createdAt?: string;
}

const roleColors: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800',
  manager: 'bg-blue-100 text-blue-800',
  viewer: 'bg-gray-100 text-gray-800',
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    const toastId = toast.loading('Loading users...');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${SUPABASE_URL}/functions/v1/list-reviewers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const { reviewers } = await response.json();
      setUsers(reviewers);
      toast.success('Users loaded', { id: toastId });
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users', { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Users</h2>
          <p className="text-sm text-gray-600 mt-1">
            Users who have access to the system via Azure AD
          </p>
        </div>
        <button
          onClick={loadUsers}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ArrowPathIcon className="w-5 h-5" />
          Refresh
        </button>
      </div>

      {users.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <UserCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
          <p className="text-gray-600">
            Users will appear here after they log in for the first time.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Active
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <UserCircleIcon className="w-10 h-10 text-gray-400" />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        {user.title && (
                          <div className="text-xs text-gray-500">{user.title}</div>
                        )}
                        {user.externalId && (
                          <div className="text-xs text-gray-400 font-mono">Entra: {user.externalId.slice(0, 8)}...</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                    {user.department && (
                      <div className="text-xs text-gray-500">{user.department}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${roleColors[user.role || 'viewer']}`}>
                      {user.role || 'viewer'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs space-y-1">
                      {user.canApprove && (
                        <div className="flex items-center text-green-600">
                          <span className="mr-1">✓</span> Can Approve
                        </div>
                      )}
                      {user.canEditTemplates && (
                        <div className="flex items-center text-blue-600">
                          <span className="mr-1">✓</span> Edit Templates
                        </div>
                      )}
                      {!user.canApprove && !user.canEditTemplates && (
                        <div className="text-gray-400">View Only</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {user.lastSignIn
                        ? new Date(user.lastSignIn).toLocaleString()
                        : 'Never'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">ℹ️ User Management</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Users are automatically added when they sign in via Azure AD</li>
          <li>• To add new users, add them to your Azure Enterprise Application</li>
          <li>• All users with access can review and approve contracts</li>
          <li>• User names are pulled from Azure AD profile metadata</li>
        </ul>
      </div>
    </div>
  );
}

